import { expandLocales, type Translate } from './localized';
import { btn, h } from './icons';
import { words } from './words';
import { getLang } from './i18n';
import { PD_MAX_MM, PD_MIN_MM } from '../pose/Sizing';
import type { SizingSelection } from '../pose/SizingSession';

let completed = false;
const copy = {
  fr: ['Pendant que votre essayage se prépare', 'Cette estimation règle l’échelle de l’aperçu. Vous pourrez la modifier ensuite.', 'Continuer', 'Votre essayage est prêt'],
  en: ['While your try-on gets ready', 'This estimate sets the preview scale. You can adjust it later.', 'Continue', 'Your try-on is ready'],
  id: ['Sambil menyiapkan coba virtual', 'Perkiraan ini mengatur skala pratinjau. Anda dapat mengubahnya nanti.', 'Lanjutkan', 'Coba virtual Anda siap'],
};

/** Loading never dismisses an unfinished calibration form. No artificial delay. */
export class HeadSetup {
  readonly root = h('section', 'mt-head-setup');
  private readonly status = h('p', 'mt-setup-status');
  get complete(): boolean { return completed; }
  constructor(private readonly host: HTMLElement, selection: () => SizingSelection,
    apply: (pd: number, manual: boolean, face: number) => void, done: () => void) {
    const w = words(), c = ALL_COPY[getLang()];
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog'); this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', w.headQuestion);
    this.status.setAttribute('role', 'status'); this.status.textContent = c[0]!;
    const title = h('h2', '', w.headQuestion);
    const faces = h('div', 'mt-seg mt-faces');
    const estimate = h('p', 'mt-setup-estimate');
    const refresh = () => {
      const s = selection(); estimate.textContent = s.manual ? w.pdKnown(s.pd) : w.pdEstimated(s.pd);
      for (const b of faces.querySelectorAll('button')) b.setAttribute('aria-pressed', String(!s.manual && Number(b.dataset.pd) === s.face));
    };
    [55, 60, 65].forEach((pd, index) => {
      const b = btn('mt-seg-item mt-face-option', w.faces[index]); b.dataset.pd = String(pd);
      b.onclick = () => { apply(pd, false, pd); refresh(); }; faces.append(b);
    });
    const form = h('form', 'mt-setup-form');
    const details = h('details'); details.append(h('summary', '', w.pdOpen));
    const label = h('label', '', `${w.pdInput} (mm)`);
    const input = h('input'); input.type = 'number'; input.inputMode = 'decimal';
    input.min = String(PD_MIN_MM); input.max = String(PD_MAX_MM); input.step = '0.5'; input.placeholder = '62';
    input.setAttribute('aria-label', w.pdInput); label.append(input); details.append(label);
    const next = h('button', 'mt-cta mt-setup-continue', c[2]); next.type = 'submit';
    form.append(details, h('p', 'mt-eng-note', c[1]), next);
    form.onsubmit = event => {
      event.preventDefault();
      if (details.open && input.value !== '') {
        if (!input.reportValidity()) return;
        apply(input.valueAsNumber, true, selection().face);
      }
      completed = true; this.hide(); this.root.remove(); done();
    };
    this.root.append(this.status, title, faces, estimate, form); host.append(this.root); refresh();
  }
  show(): void {
    if (completed) { this.root.remove(); return; }
    this.root.hidden = false; this.host.dataset.setup = '1';
    this.root.querySelector<HTMLButtonElement>('[aria-pressed="true"]')?.focus({ preventScroll: true });
  }
  engineReady(): void { this.status.textContent = ALL_COPY[getLang()][3]!; }
  hide(): void { this.root.hidden = true; delete this.host.dataset.setup; }
  destroy(): void { this.hide(); this.root.remove(); }
}

const ALL_COPY = expandLocales<string[]>(copy, (tr: Translate) => ([tr("While your try-on gets ready"), tr("This estimate sets the preview scale. You can adjust it later."), tr("Continue"), tr("Your try-on is ready")]));
